/**
 * Book My Test on the job page (2026-10-17). A job a customer asked for
 * through the reminder says so above the schedule step, with their best
 * times. When it is marked "Card needed" or "Terms requested", Schedule is
 * locked until Regina or Corbin approves, and this says why in plain words.
 * (The server refuses the schedule too; this is so nobody is surprised.)
 */
import { useQuery } from "@tanstack/react-query";
import { CalendarCheck, Lock } from "lucide-react";
import { api } from "@/lib/api";

interface BookingLock {
    bookingId: string;
    locked: boolean;
    reason: string | null;
    bestTimes: string | null;
    paymentLabel: string;
}

export function BookingScheduleBanner({ jobId, status }: { jobId: string; status: string }) {
    const q = useQuery({
        queryKey: ["book-my-test", "by-job", jobId],
        queryFn: () => api.get<{ booking: BookingLock | null }>(`/api/book-my-test/by-job/${jobId}`),
        staleTime: 30_000,
        retry: false,
    });
    const b = q.data?.booking;
    if (!b || status !== "awaiting_scheduling") return null;
    if (b.locked) {
        return (
            <div
                className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
                data-testid="booking-schedule-lock"
                role="status"
            >
                <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                    <b>Schedule is locked.</b> {b.reason}
                </span>
            </div>
        );
    }
    return (
        <div
            className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-950 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100"
            data-testid="booking-schedule-note"
            role="status"
        >
            <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
                <b>The customer asked for this test.</b>
                {b.bestTimes ? ` Best times: ${b.bestTimes}.` : ""} Nothing was promised, so confirm the day with them.{" "}
                {b.paymentLabel}.
            </span>
        </div>
    );
}
